import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, FileText, Star, Settings, LogOut, Save, Plus, Check, X, RefreshCw, Trash2, Eye, Send, EyeOff, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { useSiteContent } from '../contexts/SiteContent';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['General', 'Dental Health', 'Medical Tips', 'Wellness', 'News'];

// --- Login Screen ---
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, { password });
      sessionStorage.setItem('blog_admin', password);
      onLogin();
    } catch {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="admin-login-screen">
      <SEO title="Admin" noIndex />
      <Card className="w-full max-w-sm border-0 shadow-2xl rounded-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="bg-teal-100 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-teal-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Admin Panel</h1>
            <p className="text-slate-500 text-sm mt-1">K&K Medical & Dental</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="admin-pwd">Password</Label>
              <Input
                id="admin-pwd"
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="mt-1"
                data-testid="admin-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="admin-login-submit">
              {loading ? 'Verifying...' : 'Login'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// --- Content Editor Tab ---
const ContentEditor = ({ password }) => {
  const { content, refresh } = useSiteContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setForm({
        prices: { ...content.prices },
        hours: {
          umzinto: { ...content.hours?.umzinto },
          scottburgh: { ...content.hours?.scottburgh }
        },
        contact: { ...content.contact },
        promo: { ...content.promo }
      });
    }
  }, [content]);

  const updateField = (section, key, value) => {
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const updateNested = (section, sub, key, value) => {
    setForm(prev => ({
      ...prev,
      [section]: { ...prev[section], [sub]: { ...prev[section][sub], [key]: value } }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { ...form, password });
      refresh();
      toast.success('Content saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <div className="space-y-8" data-testid="content-editor">
      {/* Prices */}
      <Card className="border-0 shadow-md rounded-xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-teal-600" /> Consultation Prices
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Standard Consultation</Label>
              <Input value={form.prices.consultation} onChange={e => updateField('prices', 'consultation', e.target.value)} className="mt-1" data-testid="edit-price-consultation" />
            </div>
            <div>
              <Label>Skin Consult</Label>
              <Input value={form.prices.skin_consult} onChange={e => updateField('prices', 'skin_consult', e.target.value)} className="mt-1" data-testid="edit-price-skin" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Promo Text */}
      <Card className="border-0 shadow-md rounded-xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-rose-500" /> Promo & Banner Text
          </h3>
          <div className="space-y-4">
            <div>
              <Label>Hero Banner Text (Home page)</Label>
              <Input value={form.promo.hero_text} onChange={e => updateField('promo', 'hero_text', e.target.value)} className="mt-1" data-testid="edit-promo-hero" />
            </div>
            <div>
              <Label>Price Banner Text</Label>
              <Input value={form.promo.banner_text} onChange={e => updateField('promo', 'banner_text', e.target.value)} className="mt-1" data-testid="edit-promo-banner" />
            </div>
            <div>
              <Label>Home Note Text</Label>
              <Input value={form.promo.home_note} onChange={e => updateField('promo', 'home_note', e.target.value)} className="mt-1" data-testid="edit-promo-note" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card className="border-0 shadow-md rounded-xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-500" /> Contact Details
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Phone Number</Label>
              <Input value={form.contact.phone} onChange={e => updateField('contact', 'phone', e.target.value)} className="mt-1" data-testid="edit-contact-phone" />
            </div>
            <div>
              <Label>WhatsApp Number</Label>
              <Input value={form.contact.whatsapp} onChange={e => updateField('contact', 'whatsapp', e.target.value)} className="mt-1" data-testid="edit-contact-whatsapp" />
            </div>
            <div>
              <Label>WhatsApp Link Number (e.g. 27828951916)</Label>
              <Input value={form.contact.whatsapp_link} onChange={e => updateField('contact', 'whatsapp_link', e.target.value)} className="mt-1" data-testid="edit-contact-whatsapp-link" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.contact.email} onChange={e => updateField('contact', 'email', e.target.value)} className="mt-1" data-testid="edit-contact-email" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hours - Umzinto */}
      <Card className="border-0 shadow-md rounded-xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-teal-600" /> Operating Hours — Umzinto
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Weekday Hours</Label>
              <Input value={form.hours.umzinto.weekday} onChange={e => updateNested('hours', 'umzinto', 'weekday', e.target.value)} className="mt-1" data-testid="edit-hours-umzinto-weekday" />
            </div>
            <div>
              <Label>Saturday Hours</Label>
              <Input value={form.hours.umzinto.saturday} onChange={e => updateNested('hours', 'umzinto', 'saturday', e.target.value)} className="mt-1" data-testid="edit-hours-umzinto-saturday" />
            </div>
            <div>
              <Label>Sunday / Holidays</Label>
              <Input value={form.hours.umzinto.sunday} onChange={e => updateNested('hours', 'umzinto', 'sunday', e.target.value)} className="mt-1" data-testid="edit-hours-umzinto-sunday" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hours - Scottburgh */}
      <Card className="border-0 shadow-md rounded-xl">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-500" /> Operating Hours — Scottburgh
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Weekday Hours</Label>
              <Input value={form.hours.scottburgh.weekday} onChange={e => updateNested('hours', 'scottburgh', 'weekday', e.target.value)} className="mt-1" data-testid="edit-hours-scott-weekday" />
            </div>
            <div>
              <Label>Saturday Hours</Label>
              <Input value={form.hours.scottburgh.saturday} onChange={e => updateNested('hours', 'scottburgh', 'saturday', e.target.value)} className="mt-1" data-testid="edit-hours-scott-saturday" />
            </div>
            <div>
              <Label>Sunday / Holidays</Label>
              <Input value={form.hours.scottburgh.sunday} onChange={e => updateNested('hours', 'scottburgh', 'sunday', e.target.value)} className="mt-1" data-testid="edit-hours-scott-sunday" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="sticky bottom-4">
        <Button onClick={handleSave} disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 text-lg shadow-xl" data-testid="save-content-btn">
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

// --- Blog Manager Tab ---
const BlogManager = ({ password }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '', excerpt: '', content: '', author: 'K&K Medical Team',
    image_url: '', category: 'General', tags: '', published: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blog?include_drafts=true`);
      setPosts(res.data);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...newPost, tags: newPost.tags ? newPost.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      await axios.post(`${API}/blog?password=${encodeURIComponent(password)}`, payload);
      toast.success('Blog post created!');
      setShowCreateDialog(false);
      setNewPost({ title: '', excerpt: '', content: '', author: 'K&K Medical Team', image_url: '', category: 'General', tags: '', published: true });
      fetchPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create post'); }
    finally { setIsSubmitting(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${API}/blog/${id}?password=${encodeURIComponent(password)}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch { toast.error('Failed to delete post'); }
  };

  const togglePublished = async (post) => {
    try {
      const payload = {
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        author: post.author,
        image_url: post.image_url || null,
        category: post.category || 'General',
        tags: post.tags || [],
        published: !post.published,
      };
      await axios.put(`${API}/blog/${post.id}?password=${encodeURIComponent(password)}`, payload);
      toast.success(payload.published ? 'Post published!' : 'Post unpublished (saved as draft)');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update post');
    }
  };

  const viewPost = (id) => {
    window.open(`/blog/${id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="blog-manager">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-slate-900">Blog Posts ({posts.length})</h3>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="admin-create-post-btn">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />)}</div>
      ) : posts.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No blog posts yet. Create your first one!</p>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <Card key={post.id} className="border-0 shadow-sm rounded-xl" data-testid={`admin-post-${post.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{post.title}</p>
                  <p className="text-sm text-slate-500">{post.category} · {new Date(post.created_at).toLocaleDateString('en-ZA')}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${post.published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-slate-600 hover:bg-slate-50"
                    onClick={() => viewPost(post.id)}
                    title="View post in new tab"
                    data-testid={`admin-view-post-${post.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`rounded-full ${post.published ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                    onClick={() => togglePublished(post)}
                    title={post.published ? 'Unpublish (move to draft)' : 'Publish now'}
                    data-testid={`admin-toggle-publish-${post.id}`}
                  >
                    {post.published ? <EyeOff className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deletePost(post.id)} data-testid={`admin-delete-post-${post.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create New Blog Post</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-4" data-testid="admin-create-post-form">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input required value={newPost.title} onChange={e => setNewPost(p => ({...p, title: e.target.value}))} placeholder="Enter post title" data-testid="admin-input-title" />
            </div>
            <div className="space-y-2">
              <Label>Excerpt *</Label>
              <Input required value={newPost.excerpt} onChange={e => setNewPost(p => ({...p, excerpt: e.target.value}))} placeholder="Brief description" data-testid="admin-input-excerpt" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <select value={newPost.category} onChange={e => setNewPost(p => ({...p, category: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="admin-input-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input value={newPost.tags} onChange={e => setNewPost(p => ({...p, tags: e.target.value}))} placeholder="e.g. dental, tips" data-testid="admin-input-tags" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Featured Image URL</Label>
              <Input value={newPost.image_url} onChange={e => setNewPost(p => ({...p, image_url: e.target.value}))} placeholder="https://example.com/image.jpg" data-testid="admin-input-image" />
            </div>
            <div className="space-y-2">
              <Label>Content * (Markdown supported)</Label>
              <Textarea required value={newPost.content} onChange={e => setNewPost(p => ({...p, content: e.target.value}))} placeholder="Write your blog post content here..." rows={10} className="font-mono text-sm" data-testid="admin-input-content" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="pub" checked={newPost.published} onChange={e => setNewPost(p => ({...p, published: e.target.checked}))} className="rounded border-slate-300" data-testid="admin-input-published" />
              <Label htmlFor="pub" className="text-sm">Publish immediately</Label>
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="admin-submit-post">
              {isSubmitting ? 'Creating...' : 'Create Post'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Review Manager Tab ---
const ReviewManager = ({ password }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newReview, setNewReview] = useState({ author_name: '', text: '', rating: 5, time_description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchReviews(); }, [filter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reviews/moderated?status=${filter}&password=${encodeURIComponent(password)}`);
      setReviews(res.data);
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  const syncReviews = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/reviews/sync?password=${encodeURIComponent(password)}`);
      toast.success(res.data.message);
      fetchReviews();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/reviews/moderated/${id}?status=${status}&password=${encodeURIComponent(password)}`);
      toast.success(`Review ${status}`);
      setReviews(prev => prev.filter(r => r.id !== id));
    } catch { toast.error('Failed to update'); }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/reviews/moderated/${id}?password=${encodeURIComponent(password)}`);
      toast.success('Review deleted');
      setReviews(prev => prev.filter(r => r.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const addManualReview = async (e) => {
    e.preventDefault();
    if (!newReview.author_name.trim() || !newReview.text.trim()) { toast.error('Name and text required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/manual`, { ...newReview, password });
      toast.success('Review added');
      setNewReview({ author_name: '', text: '', rating: 5, time_description: '' });
      setShowAddForm(false);
      fetchReviews();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    finally { setSubmitting(false); }
  };

  const filters = ['pending', 'approved', 'rejected', 'all'];

  return (
    <div data-testid="review-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h3 className="text-lg font-bold text-slate-900">Reviews</h3>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddForm(!showAddForm)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full" data-testid="admin-add-review-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Review
          </Button>
          <Button onClick={syncReviews} disabled={syncing} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="admin-sync-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Google'}
          </Button>
        </div>
      </div>

      {/* Add Review Form */}
      {showAddForm && (
        <Card className="border-0 shadow-lg rounded-xl mb-6" data-testid="admin-add-review-form">
          <CardContent className="p-6">
            <h4 className="font-bold text-slate-900 mb-4">Add Review Manually</h4>
            <form onSubmit={addManualReview} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Reviewer Name *</Label>
                  <Input value={newReview.author_name} onChange={e => setNewReview(p => ({...p, author_name: e.target.value}))} placeholder="e.g. John Smith" className="mt-1" data-testid="admin-review-name" />
                </div>
                <div>
                  <Label>Time Description</Label>
                  <Input value={newReview.time_description} onChange={e => setNewReview(p => ({...p, time_description: e.target.value}))} placeholder="e.g. 2 weeks ago" className="mt-1" data-testid="admin-review-time" />
                </div>
              </div>
              <div>
                <Label>Rating *</Label>
                <div className="flex gap-1 mt-1" data-testid="admin-review-rating">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} type="button" onClick={() => setNewReview(p => ({...p, rating: s}))} className="p-1 hover:scale-110 transition-transform">
                      <Star className={`w-7 h-7 ${s <= newReview.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Review Text *</Label>
                <Textarea value={newReview.text} onChange={e => setNewReview(p => ({...p, text: e.target.value}))} rows={4} placeholder="Paste or type the review..." className="mt-1 resize-none" data-testid="admin-review-text" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6" data-testid="admin-review-submit">{submitting ? 'Adding...' : 'Add Review'}</Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6" data-testid="admin-review-filters">
        {filters.map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-all ${filter === f ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`} data-testid={`admin-filter-${f}`}>{f}</button>
        ))}
      </div>

      {/* Review List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />)}</div>
      ) : reviews.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No {filter !== 'all' ? filter : ''} reviews found</p>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <Card key={review.id} className="border-0 shadow-sm rounded-xl" data-testid={`admin-review-${review.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-slate-900">{review.author_name}</p>
                      <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />)}</div>
                      <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium capitalize ${review.status === 'approved' ? 'bg-green-100 text-green-700' : review.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{review.status}</span>
                    </div>
                    <p className="text-slate-600 text-sm italic line-clamp-2">"{review.text}"</p>
                    <p className="text-xs text-slate-400 mt-1">{review.relative_time_description} {review.source === 'manual' ? '· Manual' : ''}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                  {review.status !== 'approved' && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full" onClick={() => updateStatus(review.id, 'approved')} data-testid={`admin-approve-${review.id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>
                  )}
                  {review.status !== 'rejected' && (
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateStatus(review.id, 'rejected')} data-testid={`admin-reject-${review.id}`}><X className="w-4 h-4 mr-1" /> Reject</Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full text-slate-500 ml-auto" onClick={() => deleteReview(review.id)} data-testid={`admin-delete-${review.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Prize Manager Tab (Monthly Prize Draw) ---
const PrizeManager = ({ password }) => {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', image_url: '', order: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchPrizes(); }, []);

  const fetchPrizes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/prizes`);
      setPrizes(res.data || []);
    } catch { toast.error('Failed to load prizes'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', image_url: '', order: prizes.length });
    setShowCreate(true);
  };

  const openEdit = (prize) => {
    setEditing(prize);
    setForm({
      title: prize.title || '',
      description: prize.description || '',
      image_url: prize.image_url || '',
      order: prize.order || 0,
    });
    setShowCreate(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing) {
        await axios.put(`${API}/prizes/${editing.id}?password=${encodeURIComponent(password)}`, payload);
        toast.success('Prize updated');
      } else {
        await axios.post(`${API}/prizes?password=${encodeURIComponent(password)}`, payload);
        toast.success('Prize added');
      }
      setShowCreate(false);
      fetchPrizes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save prize');
    } finally { setSaving(false); }
  };

  const deletePrize = async (id) => {
    if (!window.confirm('Delete this prize?')) return;
    try {
      await axios.delete(`${API}/prizes/${id}?password=${encodeURIComponent(password)}`);
      toast.success('Prize deleted');
      fetchPrizes();
    } catch { toast.error('Failed to delete prize'); }
  };

  return (
    <div data-testid="prize-manager">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Monthly Prizes ({prizes.length})</h3>
          <p className="text-xs text-slate-500">These appear on the public /prizes page in this order.</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="admin-add-prize-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Prize
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse" />)}</div>
      ) : prizes.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No prizes yet — click <span className="font-semibold">Add Prize</span> to get started.</p>
      ) : (
        <div className="space-y-3">
          {prizes.map(prize => (
            <Card key={prize.id} className="border-0 shadow-sm rounded-xl" data-testid={`admin-prize-${prize.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                {prize.image_url ? (
                  <img src={prize.image_url} alt={prize.title} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-7 h-7 text-amber-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{prize.title}</p>
                  {prize.description && <p className="text-sm text-slate-500 truncate">{prize.description}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">Order: {prize.order}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEdit(prize)} data-testid={`admin-edit-prize-${prize.id}`}>
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full text-red-500 hover:bg-red-50" onClick={() => deletePrize(prize.id)} data-testid={`admin-delete-prize-${prize.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Prize' : 'Add Prize'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Prize Title *</Label>
              <Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. Free Dental Cleaning" data-testid="prize-input-title" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} placeholder="Briefly describe what the winner gets..." data-testid="prize-input-description" />
            </div>
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="https://example.com/prize.jpg" data-testid="prize-input-image" />
              <p className="text-xs text-slate-500">Optional. Use a public image URL (e.g. from Unsplash or your own host).</p>
            </div>
            <div className="space-y-2">
              <Label>Display Order</Label>
              <Input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} data-testid="prize-input-order" />
              <p className="text-xs text-slate-500">Lower numbers appear first.</p>
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="prize-save-btn">
              {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Add Prize')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Main Admin Panel ---
export const AdminPanel = () => {
  const [isAuth, setIsAuth] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const password = sessionStorage.getItem('blog_admin');

  useEffect(() => {
    if (password) setIsAuth(true);
  }, [password]);

  const handleLogout = () => {
    sessionStorage.removeItem('blog_admin');
    setIsAuth(false);
  };

  if (!isAuth) return <AdminLogin onLogin={() => setIsAuth(true)} />;

  const tabs = [
    { id: 'content', label: 'Content', icon: Settings },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'prizes', label: 'Prizes', icon: Trophy },
  ];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-panel">
      <SEO title="Admin Panel" noIndex />

      {/* Top Bar */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">K&K Admin Panel</h1>
            <p className="text-slate-400 text-xs">Manage your website content</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full border-slate-600 text-slate-300 hover:bg-slate-800" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1" data-testid="admin-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'content' && <ContentEditor password={password} />}
        {activeTab === 'blog' && <BlogManager password={password} />}
        {activeTab === 'reviews' && <ReviewManager password={password} />}
        {activeTab === 'prizes' && <PrizeManager password={password} />}
      </div>
    </div>
  );
};
